import React, {ButtonHTMLAttributes, forwardRef} from 'react';
import {LoadingDot} from 'Components/LoadingDot';
import {twMerge} from 'tailwind-merge';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    isLoading?: boolean;
    outlined?: boolean;
    unstyled?: boolean;
    error?: boolean;
    success?: boolean;
}

export const LoadingSmall: React.FC<{className?: string}> = ({className}) => {
    return (
        <div className={twMerge('flex w-full items-center justify-center gap-2', className)}>
            <LoadingDot
                width={10}
                height={10}
                distance={3}
                style={{animationDelay: '100ms'}}
                src={
                    'https://firebasestorage.googleapis.com/v0/b/rendition-prod.appspot.com/o/d15c753b-4219-48fa-8530-3a79357f0659.svg?alt=media&token=9d42148e-43d8-4ecb-ae4d-bbb544ab4879'
                }
            />
            <LoadingDot
                width={10}
                height={10}
                distance={3}
                style={{animationDelay: '200ms'}}
                src={
                    'https://firebasestorage.googleapis.com/v0/b/rendition-prod.appspot.com/o/c84ffc45-341a-4051-bc80-563022626d95.svg?alt=media&token=690a080c-bc29-48aa-9cb1-29df2cf1cf2a'
                }
            />
            <LoadingDot
                width={10}
                height={10}
                distance={3}
                src={
                    'https://firebasestorage.googleapis.com/v0/b/rendition-prod.appspot.com/o/78126f4c-056f-4978-8154-46539af1ae41.svg?alt=media&token=6594c16e-7362-47ab-bac5-f9256683dab3'
                }
            />
        </div>
    );
};

const baseClasses =
    'h-10 px-4 py-2 basis-10 shrink-0 font-semibold text-[13px] text-white bg-transparent border rounded flex justify-center items-center';
const getDisabledStyle = (outlined?: boolean) =>
    outlined
        ? 'border-neutral-500 hover:bg-none opacity-50'
        : 'border-transparent opacity-50 bg-neutral-500 hover:border-white';
const getButtonStyle = (outlined?: boolean) =>
    outlined
        ? 'border-sky-500 hover:border-transparent hover:bg-sky-500'
        : 'border-transparent bg-sky-500 hover:border-white';
const getErrorStyle = (outlined?: boolean) =>
    outlined ? 'border-red-500' : 'border-transparent opacity-70 bg-red-500';

const getErrorHover = (outlined?: boolean, disabled?: boolean) =>
    outlined ? 'hover:bg-red-500' : 'hover:border-white';
const getSuccessStyle = (outlined?: boolean) =>
    outlined ? 'border-green-400 opacity-70 hover:bg-green-400/30' : 'border-transparent opacity-70 bg-green-400';

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({isLoading, className, success, outlined, unstyled, error, ...props}, ref) => {
        return (
            <button
                ref={ref}
                {...props}
                className={
                    unstyled
                        ? className
                        : twMerge(
                              baseClasses,
                              !props.disabled && !error && getButtonStyle(outlined),
                              props.disabled && !error && getDisabledStyle(outlined),
                              error && getErrorStyle(outlined),
                              error && getErrorHover(outlined, props.disabled),
                              success && getSuccessStyle(outlined),
                              className || ''
                          )
                }
                disabled={props.disabled || isLoading}
            >
                {isLoading ? <LoadingSmall /> : props.children}
            </button>
        );
    }
);

export default Button;
